<template>
  <div class="wrapper">
    <div
      ref="actions"
      v-bind:class="{ active: selected }"
      class="group-actions"
    >
      <div
        @click="onGroupClick"
        v-if="!isShowEditor"
        :style="readonly ? 'margin-top: 15px' : ''"
        class="flex pl-3 align-center"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <div v-on="on" class="group-name font-weight-bold">
              {{ group.name }}
            </div>
          </template>
          {{ group.name }}
        </v-tooltip>
        <v-switch
          :disabled="readonly"
          v-model="isActive"
          v-on:click.native.stop
          color="info"
        />
        <v-tooltip v-if="!readonly" top>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" @click="removeLocationGroup">delete</v-icon>
          </template>
          地点グループを削除
        </v-tooltip>
        <v-menu v-if="!readonly" bottom offset-x>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" v-on:click.stop icon>
              <v-icon color="rgba(0,0,0,.54)">more_vert</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-tile
              v-if="hasExpandingList"
              @click="showLocationBulkAddModal"
            >
              <v-icon>add_location</v-icon>
              <v-list-tile-content class="ml-2">
                <v-list-tile-title>地点一括追加</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile v-else @click="isShowPoiSettingModal = true">
              <v-icon>add_location</v-icon>
              <v-list-tile-content class="ml-2">
                <v-list-tile-title>地点を変更</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile @click="showNameEditor">
              <v-icon>edit</v-icon>
              <v-list-tile-content class="ml-2">
                <v-list-tile-title>グループ名編集</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile @click="showCopyModal">
              <v-icon>file_copy</v-icon>
              <v-list-tile-content class="ml-2">
                <v-list-tile-title>グループのコピー</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-menu>
      </div>
      <div v-else class="pa-2 group-name-edit-body">
        <v-form ref="form" @submit.prevent v-model="valid">
          <v-text-field
            v-model="editingName"
            @click.native.stop
            :rules="[groupValidations.required, groupValidations.max]"
            dark
            single-line
            autofocus
            color="white"
          />
        </v-form>
        <div class="flex justify-center align-center py-1">
          <AppButtonCancel @click.native.stop="closeNameEditor" small>
            キャンセル
          </AppButtonCancel>
          <AppButtonOk @click.native.stop="applyName" small>
            適用
          </AppButtonOk>
        </div>
      </div>
      <span v-if="group.status === 'active'" class="location-count">
        {{ getActiveLocationCount() }} / {{ getAllLocationCount() }}地点
      </span>
      <span v-if="group.status === 'inactive'" class="location-count">
        - / {{ getAllLocationCount() }}地点
      </span>
      <div v-if="!isEnableBulkSetting" class="pa-2 flex align-center">
        <span class="pr-2 group-radius-label">半径</span>
        <div v-bind:class="{ inactive: enableBulkSetting }">
          <v-text-field
            v-model.number="radius"
            @wheel.prevent
            :disabled="readonly || enableBulkSetting"
            :rules="[
              menuSettingValidations.required,
              menuSettingValidations.min,
              menuSettingValidations.max,
            ]"
            type="number"
          >
            <template v-slot:append-outer>
              <span>m</span>
            </template>
          </v-text-field>
        </div>
      </div>
    </div>
    <TransitionExpand>
      <SidebarPointList v-if="selected && hasExpandingList">
        <template v-if="hasLocations">
          <SidebarPoint
            v-for="location in locations"
            :key="location.id"
            :group="group"
            :location="location"
            :readonly="readonly"
          />
        </template>
        <template v-else>
          <div class="pl-2 py-2">地点を登録してください</div>
        </template>
      </SidebarPointList>
    </TransitionExpand>
    <SidebarPoiGroupSettingModal
      v-if="isShowPoiSettingModal"
      :mode="mode"
      :isFetchSimulationPoiLocations="isFetchSimulationPoiLocations"
      @save="update"
      @cancel="isShowPoiSettingModal = false"
    />
    <CopyModal
      :simulation-location-groups="simulationLocationGroups"
      :group-id="group.id"
      v-model="isShowCopyModal"
    />
    <LocationBulkAddModal
      v-if="isShowLocationBulkAddModal"
      v-model="isShowLocationBulkAddModal"
      :location-group="group"
    />
  </div>
</template>

<script>
import TransitionExpand from '@/components/Shared/Transition/TransitionExpand'
import SidebarPointList from '@/components/Shared/Simulation/SimulationContentDeliverySidebarPointList'
import SidebarPoint from '@/components/Shared/Simulation/SimulationContentDeliverySidebarPoint'
import { mapActions, mapGetters } from 'vuex'
import CopyModal from './SimulationContentDeliveryLocationGroupCopyModal'
import LocationBulkAddModal from './SimulationContentDeliveryContentLocationBulkAddModal'
import { SimulationLocationGroup } from '@/models/Simulation'
import { locationGroupType } from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/constants'
import SidebarPoiGroupSettingModal from '@/components/Shared/Simulation/SimulationContentDeliverySidebarPoiGroupSettingModal'

export default {
  name: 'SimulationContentDeliverySidebarGroup',
  components: {
    CopyModal,
    LocationBulkAddModal,
    SidebarPoint,
    SidebarPointList,
    TransitionExpand,
    SidebarPoiGroupSettingModal,
  },
  props: {
    group: {
      type: Object,
      default: null,
    },
    form: {
      type: Object,
      default: () => {},
    },
    simulationLocationGroups: {
      type: Object,
      default: () => {},
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isFetchSimulationPoiLocations: false,
      isShowCopyModal: false,
      isShowEditor: false,
      isShowLocationBulkAddModal: false,
      isShowPoiSettingModal: false,
      editingName: '',
      valid: false,
      hasExpandingList: this.group.type === locationGroupType.CUSTOM_LOCATION,
    }
  },
  computed: {
    ...mapGetters('simulation', ['selectedGroupId']),
    groupValidations() {
      return SimulationLocationGroup.validations
    },
    selected() {
      return this.group.id === this.selectedGroupId
    },
    isActive: {
      get() {
        return this.group.isActive()
      },
      set(value) {
        this.group.updateStatus(value)
      },
    },
    hasLocations() {
      return this.group.hasLocations
    },
    locations() {
      return this.group.simulationLocations
    },
    enableBulkSetting() {
      return this.menuSetting.enableBulkSetting
    },
    menuSettingValidations() {
      return this.menuSetting.validations.radiusSetting
    },
    menuSetting() {
      return this.form.deliverySetting.menuSetting
    },
    isEnableBulkSetting() {
      return this.menuSetting.enableBulkSetting
    },
    radius: {
      get() {
        return this.group.radius
      },
      set(radius) {
        this.group.updateRadius(radius)
      },
    },
    mode() {
      return {
        type: 'edit',
        text: '編集',
      }
    },
  },
  methods: {
    ...mapActions('simulation', {
      selectGroup: 'SELECT_GROUP',
      deselectGroup: 'DESELECT_GROUP',
    }),
    async onGroupClick() {
      this.toggleSelectGroup()
      this.changeIsSelectedPoiLocationGroup()
    },
    toggleSelectGroup() {
      if (this.selected) {
        this.deselectGroup()
      } else {
        this.selectGroup(this.group)
      }
    },
    async removeLocationGroup() {
      const result = await this.$confirm(
        `地点グループ ${this.group.name} を削除します。よろしいですか？`,
      )
      if (result) {
        this.simulationLocationGroups.removeLocationGroup(this.group.id)
      }
    },
    showCopyModal() {
      this.isShowCopyModal = true
    },
    showLocationBulkAddModal() {
      this.isShowLocationBulkAddModal = true
    },
    showNameEditor() {
      this.editingName = this.group.name
      this.isShowEditor = true
    },
    closeNameEditor() {
      this.isShowEditor = false
    },
    applyName() {
      if (!this.$refs.form.validate()) {
        return false
      }
      this.group.name = this.editingName
      this.closeNameEditor()
    },
    update(poiSearchForm) {
      try {
        this.isFetchSimulationPoiLocations = true
        this.group.updatePoiConditions(poiSearchForm)
        this.isFetchSimulationPoiLocations = false
        this.isShowPoiSettingModal = false
      } catch (e) {
        this.isFetchSimulationPoiLocations = false
      }
    },
    changeIsSelectedPoiLocationGroup() {
      this.$emit(
        'changeIsSelectedPoiLocationGroup',
        this.selected && this.group.type === locationGroupType.POI_LOCATION,
      )
    },
    getActiveLocationCount() {
      return this.group.simulationLocations.filter(
        location => location.status === 'active',
      ).length
    },
    getAllLocationCount() {
      return this.group.simulationLocations.length
    },
  },
}
</script>

<style lang="scss" scoped>
$border: #d5d5d5;
$border-active: #66b2ff;
$body-active: #66b2ff;

::v-deep .v-input__slot,
::v-deep.v-input--selection-controls:not(.v-input--hide-details)
  .v-input__slot {
  margin-bottom: 0;
}
.group-name-edit-body ::v-deep .v-text-field__slot {
  background-color: $body-active;
  font-weight: bold;
}

.wrapper {
  cursor: pointer;
  border-bottom: 1px solid $border;
}
.flex {
  display: flex;
}
.group {
  border-bottom: 1px solid $border;
}
.group-actions {
  position: sticky;
  top: 0;
  z-index: 1;
}
.group-name {
  margin-left: 16px;
  margin-right: auto;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.group-radius-label {
  width: 40px;
}
.group-name-edit-body {
  background: $body-active;
}
.active {
  background-color: $body-active;
}
.location-count {
  margin-left: 16px;
}
</style>
